<script lang="ts" setup>
import { ChevronDownIcon } from "@heroicons/vue/24/outline";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { type ACL } from "../pinia/auth";
import {
  agenticGrantStatusChipClass,
  agenticGrantStatusKind,
  agenticGrantStatusLabelKey,
  type AgenticGrantStatusKind,
} from "../shared/services/agenticGrantStatus";

const props = defineProps<{
  aclIds: string[];
  acls: ACL[];
  // Per-knoc live grant status (approved agents); adds a status label per knoc.
  aclStatuses?: { aclID: string; status: number }[];
}>();

const { t } = useI18n();
const open = ref(false);

// Alphabetical, so the chips read the same regardless of selection order.
const reachable = computed(() =>
  props.aclIds
    .map((id) => props.acls.find((a) => a.ACLID === id))
    .filter((a): a is ACL => Boolean(a))
    .sort((a, b) => (a.Description ?? "").localeCompare(b.Description ?? "", undefined, { sensitivity: "base" })),
);

const statusKindByAcl = computed(() => {
  const map: Record<string, AgenticGrantStatusKind> = {};
  for (const s of props.aclStatuses ?? []) {
    map[s.aclID] = agenticGrantStatusKind(s.status);
  }
  return map;
});
</script>

<template>
  <div v-if="reachable.length > 0" class="pl-4">
    <button
      type="button"
      :aria-expanded="open"
      class="inline-flex items-center gap-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      @click="open = !open"
    >
      <span>{{ t("accessMenu.canReach", { count: reachable.length }, reachable.length) }}</span>
      <ChevronDownIcon class="h-3 w-3 transition-transform" :class="{ 'rotate-180': open }" aria-hidden="true" />
    </button>
    <div v-if="open" class="mt-2 flex flex-wrap gap-1.5">
      <span
        v-for="acl in reachable"
        :key="acl.ACLID"
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-1.5 py-0.5 text-xs font-medium"
      >
        <font-awesome-icon :icon="acl.Icon" fixed-width class="h-3 w-3" />
        {{ acl.Description }}
        <span
          v-if="statusKindByAcl[acl.ACLID]"
          class="inline-flex items-center rounded-full border px-1.5 text-[0.65rem]"
          :class="agenticGrantStatusChipClass(statusKindByAcl[acl.ACLID])"
        >
          {{ t(agenticGrantStatusLabelKey(statusKindByAcl[acl.ACLID])) }}
        </span>
      </span>
    </div>
  </div>
</template>
